import React from 'react';
import { useTranslation } from 'react-i18next';
import compose from 'lodash/fp/compose';
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import { withTranslation, withUserPreferences, withAccount } from 'components/hoc';
import HereForYou from '../../HomePage/hereForYou/index';
import styled, { css } from 'styled-components';
import { AuthGatedTheme, useEffectiveDark } from '../common/AuthGatedTheme';

// Theme scoped CSS variables are provided by AuthGatedTheme; no local ThemeScope needed.

const FaqWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 32px 96px;
  box-sizing: border-box;
  background: ${({ $dark, isAuth }) =>
    $dark
      ? 'linear-gradient(180deg, #0e1526 0%, #1c2741 100%)'
      : isAuth
      ? 'linear-gradient(180deg, #edf1f9 0%, #f8fafc 100%)'
      : 'linear-gradient(180deg, #eef2ff 0%, #f9fbff 100%)'};

  @media (max-width: 992px) {
    padding: 48px 24px 80px;
  }

  @media (max-width: 768px) {
    padding: 36px 18px 72px;
  }

  @media (max-width: 540px) {
    padding: 28px 16px 60px;
  }
`;

const FaqCard = styled.div`
  width: 100%;
  max-width: 980px;
  background: var(--admincat-color-grey-1);
  border-radius: 18px;
  box-shadow: 0 24px 48px rgba(76, 129, 190, 0.16);
  padding: 48px 44px 56px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    padding: 36px 28px 44px;
    border-radius: 14px;
  }

  @media (max-width: 540px) {
    padding: 28px 18px 36px;
    border-radius: 12px;
  }
`;

const FaqHeading = styled.h1`
  margin: 0;
  font-size: 2.5rem;
  line-height: 1.2;
  color: var(--admincat-color-grey-2);
  text-align: center;

  @media (max-width: 768px) {
    font-size: 2.1rem;
  }

  @media (max-width: 540px) {
    font-size: 1.85rem;
  }
`;

const AccordionButtonContent = styled.span`
  display: flex;
  align-items: flex-start;
  gap: 18px;
  width: 100%;
  color: var(--admincat-color-grey-2);
`;

const QuestionNumber = styled.span`
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: linear-gradient(135deg, #4c81be 0%, #3a6ba5 100%);
  color: #ffffff;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 12px 20px rgba(58, 107, 165, 0.25);
`;

const QuestionText = styled.span`
  flex: 1;
  display: block;
  color: var(--admincat-color-grey-2);
  line-height: 1.6;
  font-weight: 600;
  font-size: 1.02rem;
  letter-spacing: -0.01em;
`;

const ChevronIcon = styled.span`
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border-right: 2px solid #3a6ba5;
  border-bottom: 2px solid #3a6ba5;
  transform: rotate(-45deg);
  transition: transform 0.2s ease, border-color 0.2s ease;
  margin-top: 6px;
`;

const StyledAccordion = styled(Accordion)`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .accordion__item {
    border-radius: 14px;
    overflow: hidden;
    border: 1px solid rgba(76, 129, 190, 0.12);
    box-shadow: 0 14px 32px rgba(76, 129, 190, 0.15);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    background: var(--admincat-color-grey-1);
  }

  .accordion__item:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 44px rgba(76, 129, 190, 0.18);
  }

  .accordion__button {
    display: flex;
    background: linear-gradient(135deg, #f4f7fd 0%, #eef3fc 100%);
    border: none;
    width: 100%;
    text-align: left;
    padding: 20px 24px;
    cursor: pointer;
    color: var(--admincat-color-grey-2);
  }

  .accordion__button:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 3px rgba(76, 129, 190, 0.35);
    border-radius: 14px;
  }

  .accordion__button:before {
    display: none;
  }

  .accordion__button[aria-expanded='true'] {
    background: linear-gradient(135deg, #e9f1ff 0%, #f5f9ff 100%);
  }

  .accordion__button[aria-expanded='true'] ${QuestionNumber} {
    background: linear-gradient(135deg, #3a6ba5 0%, #2d5695 100%);
  }

  .accordion__button[aria-expanded='true'] ${ChevronIcon} {
    transform: rotate(135deg);
    border-color: #2d5695;
  }

  .accordion__panel {
    background: var(--admincat-color-grey-1);
    padding: 0 24px 24px;
  }

  .accordion__panel p {
    font-size: 1rem;
    line-height: 1.75;
    color: var(--admincat-color-grey-2);
    margin: 18px 0 0;
  }

  /* Ensure dark-mode styles take precedence even when expanded */
  ${({ $dark }) =>
    $dark &&
    css`
      .accordion__button {
        background: linear-gradient(135deg, #172033 0%, #0f1524 100%);
        color: var(--admincat-color-grey-2);
      }

      .accordion__button[aria-expanded='true'] {
        background: linear-gradient(135deg, #1d2740 0%, #121a2c 100%);
      }
    `}

  @media (max-width: 768px) {
    gap: 14px;

    .accordion__button {
      padding: 18px 20px;
    }

    ${AccordionButtonContent} {
      gap: 14px;
    }

    ${QuestionNumber} {
      width: 34px;
      height: 34px;
      font-size: 0.9rem;
    }

    ${QuestionText} {
      font-size: 0.98rem;
    }

    .accordion__panel {
      padding: 0 20px 20px;
    }

    .accordion__panel p {
      font-size: 0.95rem;
    }
  }

  @media (max-width: 540px) {
    .accordion__button {
      padding: 16px 16px;
    }

    ${QuestionNumber} {
      width: 32px;
      height: 32px;
      font-size: 0.86rem;
      border-radius: 10px;
    }

    ${QuestionText} {
      font-size: 0.94rem;
      line-height: 1.55;
    }

    .accordion__panel {
      padding: 0 16px 18px;
    }

    .accordion__panel p {
      font-size: 0.9rem;
    }
  }
`;

const HelperWrapper = styled.div`
  width: 100%;
  max-width: 980px;
  margin-top: 64px;

  @media (max-width: 768px) {
    margin-top: 48px;
  }

  @media (max-width: 540px) {
    margin-top: 36px;
  }
`;

const FaqContent = ({ translate, isDarkMode }) => {
  const faqs = [
    {
      question: 'What is AdminCat? And why should I use AdminCat?',
      answer:
        'AdminCat is a unique internet service solution for private households striving to completely digitalize and interconnect your paperwork and personal data. This way you can save administrative time and get a more powerful control over your entire life.',
    },
    {
      question: 'Which key features does AdminCat offer?',
      answer:
        'With the integrated Email-Service, attached documents can get saved automatically where they belong and with the right categorization. An OCR text extraction allows a quick and easy full-text search. AdminCat also allows managing contracts and financial data in over 50 currencies with daily exchange rate updates. Reminders make you aware of upcoming expiration of contracts and important documents. And as a cloud service you can reach AdminCat from anywhere and at anytime. But the best is still to come as AdminCat is seeking to be your all-in-one admin solution.',
    },
    {
      question: 'Which limitations at AdminCat do I need to know?',
      answer:
        'AdminCat currently offers a data storage of up to 1 GB per workspace. Uploads are possible for documents not greater than 10 MB. And excessively sending emails (>300 a day) are not allowed. For this please also see our Terms and Conditions.',
    },
    {
      question: 'How much does it cost to use AdminCat?',
      answer:
        'We offer a 30-days free of charge trial period to every user initially. After that all of the AdminCat services and features are offered for a monthly rate of 2 € or a yearly rate of only 19 €.',
    },
    {
      question: 'Is there a notice period for cancelling the contract?',
      answer:
        'No, the contract is valid until the end of the prepayment period. This means that your payment alone defines the end of the contract. All user accounts are kept anonymous and hence it is technically not foreseen to offer a premature contract cancellation and payback option.',
    },
    {
      question:
        'Does AdminCat automatically delete my data after the contract has ended?',
      answer:
        "No. All your workspaces that still have at least one valid and subscribed user will continue to exist. If you haven't assigned a new admin for them, the system will choose a subscribed successor automatically. Workspaces, which are assigned to inactive users only, will also remain in the system - for another two months. During that time, the first user to renew his or her subscription will become the new admin of such a space.",
    },
    {
      question:
        'What happens with spaces that have no admin for more than two months?',
      answer:
        'Spaces abandoned for more than two months will get deleted automatically. An automatic and immediate deletion also takes place, if all assigned users have already deleted their accounts.',
    },
    {
      question: 'Are users also getting deleted automatically?',
      answer:
        'Inactive (unsubscribed) users, that have no active space assignment, will get deleted automatically after one month. This concerns users for instance, which either never completed the registration or space acceptance process or whose spaces got deleted because of the two months inactivity described above.',
    },
    {
      question: 'Is my data safe on AdminCat?',
      answer:
        "For security reasons we constantly keep our software packages up-to-date to avoid exploits. Additionally to the standard encryption of our server provider, we encrypt your data a second time, such that even our staff can't read it. High password standards, a Two-Factor-Authentication (TOTP), and the automatic session expiration after inactivity, protects all user accounts. We use the secure HTTPS protocol for data transmissions through the internet of course and daily backups are stored for the unlikely event of a major data loss.",
    },
    {
      question: 'Do you recommend additional security measures for users?',
      answer:
        'We recommend users to obey general security rules concerning password protection and special prudence against malicious phishing mails. Additionally you can export your emails from AdminCat to your local machine manually or establish a synchronization with your Email program as an own backup.',
    },
    {
      question: 'Are you subject to the GDPR standard in the EU?',
      answer:
        'Yes, AdminCat exclusively runs on servers located in Frankfurt (Germany) and is therefore subject to one of the highest privacy-protecting regulations in the world – the GDPR of the European Union. For this reason we have contracted an external data security officer, which is regularly checking, monitoring and improving our security processes.',
    },
    {
      question: 'Does AdminCat also offer access via App?',
      answer:
        'Not yet. At first we want you to enjoy the generous user experience that our website offers for large-screen devices. But we are planning to introduce a mobile app in the future as well.',
    },
    {
      question: 'How should I get started with my new account and Space?',
      answer:
        'Add persons, person groups and favorite organizations. Once defined, these units can get assigned to your Space, your documents and data like contracts and financial transactions. Once logged in you can also check out our Quick-Tour videos for more recommendations. Or you visit our channel series on YouTube.',
    },
    {
      question: 'Does every person as member of a Space need a user account?',
      answer:
        "No – like in a household, where only one or two people actively do the paperwork for all. In fact, a user may even be a third person taking care for the household's administration.",
    },
    {
      question: 'What is the basis for the data on the Dashboard?',
      answer:
        'Each Space represents a person group, which may be the members of a household or another type of personal economic unit. The dashboard displays data corresponding to this person group. If the person group members change, the financial data on the dashboard changes too.',
    },
    {
      question: 'How can I contact AdminCat in case of questions or feedback?',
      answer:
        'As an active user you can find the option "Contact us" in the user menu on the top right. Else you can also write to support@admincat.net directly. We appreciate your valuable comments and feedback, which will of course play an important role in our further product development. But please be aware, that we can only give general support and guidance, as your data is invisible for us on the database.',
    },
    {
      question: 'Could I join the AdminCat team?',
      answer:
        'Of course! We are honored by your interest. Just send us an email and describe your main experiences and biggest strengths and we will come back to you.',
    },
  ];

  return (
    <>
      <FaqCard>
        <FaqHeading>FAQs</FaqHeading>
        <StyledAccordion allowZeroExpanded $dark={isDarkMode}>
          {faqs.map((faq, index) => (
            <AccordionItem key={faq.question}>
              <AccordionItemHeading>
                <AccordionItemButton>
                  <AccordionButtonContent>
                    <QuestionNumber>
                      {String(index + 1).padStart(2, '0')}
                    </QuestionNumber>
                    <QuestionText>{translate(faq.question)}</QuestionText>
                    <ChevronIcon aria-hidden="true" />
                  </AccordionButtonContent>
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel>
                <p>{translate(faq.answer)}</p>
              </AccordionItemPanel>
            </AccordionItem>
          ))}
        </StyledAccordion>
      </FaqCard>
      <HelperWrapper>
        <HereForYou />
      </HelperWrapper>
    </>
  );
};

const Faq = (props) => {
  const {
    tr,
    account: {
      user: { isAuth },
    },
  } = props;
  const { t } = useTranslation();

  React.useEffect(() => {
    window.scroll(0, 0);
  }, []);

  // Use tr for authenticated users, t for non-authenticated users
  const translate = isAuth ? tr : t;

  const effectiveDark = useEffectiveDark(isAuth);

  return (
    <AuthGatedTheme isAuth={isAuth}>
      <FaqWrapper isAuth={isAuth} $dark={effectiveDark}>
        <FaqContent translate={translate} isDarkMode={effectiveDark} />
      </FaqWrapper>
    </AuthGatedTheme>
  );
};

export default compose(withTranslation, withUserPreferences, withAccount)(Faq);
